// إدارة المسارات التدريبية — الربط بالجدارة، ترتيب التحديات، الجمهور المستهدف، ومعاينة Preview As
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { errText } from "@/v2/lib/hooks";
import {
  GlassCard, Chip, Skeleton, Modal, PageHead, Confirm, Empty, AudiencePicker, AudienceChips, EMP_TYPES, Bar,
} from "@/v2/admin/ui";
import {
  Route, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Eye, Layers, Link2, Target, Sparkles,
} from "lucide-react";

const STATUS = { active: "مفعّل", inactive: "معطّل", archived: "مؤرشف" };
const empty = { name: "", desc: "", image: "", audience: [], status: "active", competency_id: "", bonus_points: 0, badge: "" };

function PathModal({ item, comps, onClose, onSaved }) {
  const [f, setF] = useState(item ? { ...empty, ...item } : { ...empty, competency_id: comps[0]?.id || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.name.trim().length < 3) return toast.error("اسم المسار مطلوب");
    if (!f.competency_id) return toast.error("اختر الجدارة");
    setBusy(true);
    try {
      const body = { name: f.name.trim(), desc: f.desc, image: f.image, audience: f.audience, cascade_audience: true,
                     status: f.status, competency_id: f.competency_id, bonus_points: Number(f.bonus_points) || 0, badge: f.badge };
      const response = item
        ? await api.patch(`/admin/paths/${item.id}`, body)
        : await api.post("/admin/paths", body);
      const moved = Boolean(response.data?.moved);
      const targetName = comps.find((c) => c.id === f.competency_id)?.name;
      toast.success(moved ? `تم نقل المسار إلى «${targetName}»` : item ? "تم تحديث المسار" : "تم إنشاء المسار");
      await onSaved();
      onClose();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={item ? "تعديل مسار" : "مسار تدريبي جديد"} onClose={onClose} testId="path-modal">
      <div className="space-y-4">
        <div><label className="v2-label">اسم المسار</label>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="path-name" className="v2-input" /></div>
        <div><label className="v2-label">الجدارة</label>
          <select value={f.competency_id} onChange={(e) => setF({ ...f, competency_id: e.target.value })} data-testid="path-comp" className="v2-input">
            {comps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div><label className="v2-label">الوصف</label>
          <textarea rows={2} value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} data-testid="path-desc" className="v2-input resize-none" /></div>
        <div><label className="v2-label">رابط صورة (اختياري)</label>
          <input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} data-testid="path-image" className="v2-input" dir="ltr" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="v2-label">نقاط إضافية عند الإكمال</label>
            <input type="number" value={f.bonus_points} onChange={(e) => setF({ ...f, bonus_points: e.target.value })} data-testid="path-bonus" className="v2-input" /></div>
          <div><label className="v2-label">الحالة</label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="path-status" className="v2-input">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
        </div>
        <div><label className="v2-label">الجمهور المستهدف</label>
          <AudiencePicker value={f.audience} onChange={(a) => setF({ ...f, audience: a })} testId="path-audience" />
          <p className="mt-2 text-xs text-[#bcc8cf]">يُطبّق الاختيار تلقائيًا على جميع تحديات هذا المسار حتى يظهر المسار كاملًا للفئة المحددة.</p></div>
      </div>
      <button onClick={save} disabled={busy} data-testid="path-save" className="v2-btn w-full mt-5">حفظ</button>
    </Modal>
  );
}

function AssignModal({ challenge, paths, onClose, onSaved }) {
  const [pid, setPid] = useState(challenge.path_id || "");
  const [aud, setAud] = useState(challenge.audience || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/challenges/${challenge.id}/placement`, { path_id: pid, audience: aud });
      const targetName = paths.find((p) => p.id === pid)?.name;
      toast.success(pid ? `تم ربط التحدي بمسار «${targetName}»` : "تم نقل التحدي إلى التحديات العامة");
      await onSaved();
      onClose();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`ربط التحدي — ${challenge.title}`} onClose={onClose} testId="assign-modal">
      <div className="space-y-4">
        <div><label className="v2-label">المسار التدريبي</label>
          <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="assign-path" className="v2-input">
            <option value="">— بدون مسار (تحديات عامة) —</option>
            {paths.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
        <div><label className="v2-label">الجمهور المستهدف لهذا التحدي</label>
          <AudiencePicker value={aud} onChange={setAud} testId="assign-audience" /></div>
      </div>
      <button onClick={save} disabled={busy} data-testid="assign-save" className="v2-btn w-full mt-5">
        {busy ? "جارٍ نقل التحدي..." : "حفظ"}
      </button>
    </Modal>
  );
}

function PreviewModal({ type, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["preview-journey", type],
    queryFn: async () => (await api.get(`/admin/preview/journey?as_type=${type}`)).data,
  });
  const label = (EMP_TYPES.find((t) => t.key === type) || {}).label;
  return (
    <Modal title={`معاينة كما يراها: ${label}`} onClose={onClose} wide testId="preview-modal">
      {isLoading ? <Skeleton className="h-56 w-full" /> : (data?.competencies || []).length === 0 ? (
        <p className="text-sm text-[#bcc8cf]" data-testid="preview-empty">لا يوجد محتوى مخصص لهذه الفئة حاليًا.</p>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-[#bcc8cf]">إجمالي المحطات المرئية: {data.totals.total}</p>
          {data.competencies.map((c) => (
            <div key={c.id} className="v2-inset p-4" data-testid={`preview-comp-${c.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Target size={15} className="text-[#91cab8]" />
                <b>{c.name}</b>
                <AudienceChips value={c.audience || []} />
              </div>
              {c.paths.map((p) => (
                <div key={p.id} className="mt-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Layers size={13} className="text-[#e3c799]" />
                    <span className="font-bold">{p.name}</span>
                    <Chip tone="muted">{p.total} محطة</Chip>
                  </div>
                  <ul className="mt-2 space-y-1 ps-6 list-disc text-xs text-[#bcc8cf]">
                    {p.challenges.map((ch) => <li key={ch.id}>{ch.title}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function Paths() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/admin/catalog")).data });
  const [modal, setModal] = useState(null);
  const [assign, setAssign] = useState(null);
  const [preview, setPreview] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["catalog"] }),
      qc.invalidateQueries({ queryKey: ["preview-journey"] }),
      qc.invalidateQueries({ queryKey: ["journey"] }),
      qc.invalidateQueries({ queryKey: ["challenges"] }),
    ]);
    await qc.refetchQueries({ queryKey: ["catalog"], type: "active" });
  };

  const comps = data?.competencies || [];
  const allPaths = comps.flatMap((c) => c.paths || []);

  const movePath = async (paths, i, dir) => {
    const arr = [...paths];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    try { await api.post("/admin/paths/reorder", { ids: arr.map((p) => p.id) }); await refresh(); }
    catch (e) { toast.error(errText(e)); }
  };
  const moveChallenge = async (pid, list, i, dir) => {
    const arr = [...list];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    try { await api.post(`/admin/paths/${pid}/challenges/reorder`, { ids: arr.map((c) => c.id) }); await refresh(); }
    catch (e) { toast.error(errText(e)); }
  };
  const removePath = async () => {
    try { await api.delete(`/admin/paths/${delTarget.id}`); toast.success("تم حذف المسار"); await refresh(); }
    catch (e) { toast.error(errText(e)); } finally { setDelTarget(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-paths">
      <PageHead icon={Route} title="المسارات التدريبية" sub="الجدارة ← المسار ← التحديات، مع تحديد الجمهور المستهدف لكل عنصر.">
        <button onClick={() => setModal({})} data-testid="path-create" className="v2-btn v2-btn-sm"><Plus size={15} /> مسار جديد</button>
      </PageHead>

      <GlassCard className="p-4 flex items-center gap-3 flex-wrap" data-testid="preview-bar">
        <span className="text-sm font-bold flex items-center gap-2"><Eye size={16} className="text-[#e3c799]" /> معاينة كما يراها:</span>
        {EMP_TYPES.map((t) => (
          <button key={t.key} onClick={() => setPreview(t.key)} data-testid={`preview-as-${t.key}`} className="v2-btn-ghost v2-btn-sm">
            {t.label}
          </button>
        ))}
      </GlassCard>

      {isLoading ? <Skeleton className="h-72 w-full" /> : comps.length === 0 ? <Empty text="أنشئ جدارة أولًا ثم أضف مساراتها." /> : (
        <div className="space-y-6">
          {comps.map((c) => (
            <div key={c.id} className="space-y-3" data-testid={`paths-comp-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Target size={17} className="text-[#91cab8]" />
                <h2 className="text-lg font-extrabold">{c.name}</h2>
                <AudienceChips value={c.audience || []} />
              </div>
              {(c.paths || []).length === 0 ? (
                <p className="text-sm text-[#bcc8cf] ps-6">لا توجد مسارات في هذه الجدارة بعد.</p>
              ) : c.paths.map((p, pi) => (
                <GlassCard key={p.id} className="p-5" data-testid={`path-card-${p.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <Layers size={18} className="text-[#e3c799] mt-1" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-extrabold">{p.name}</h3>
                        <Chip tone={p.status === "active" ? "emerald" : "muted"}>{STATUS[p.status] || p.status}</Chip>
                        <AudienceChips value={p.audience || []} />
                        <Chip tone="muted">{(p.challenges || []).length} تحديًا</Chip>
                        {p.bonus_points > 0 && <Chip tone="gold"><Sparkles size={11} /> +{p.bonus_points} نقطة</Chip>}
                      </div>
                      {p.desc && <p className="text-xs text-[#bcc8cf] mt-1">{p.desc}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setModal(p)} data-testid={`path-edit-${p.id}`} className="v2-btn-ghost v2-btn-sm"><Pencil size={13} /></button>
                      <button onClick={() => movePath(c.paths, pi, -1)} disabled={pi === 0} className="v2-btn-ghost v2-btn-sm disabled:opacity-30"><ChevronUp size={13} /></button>
                      <button onClick={() => movePath(c.paths, pi, 1)} disabled={pi === c.paths.length - 1} className="v2-btn-ghost v2-btn-sm disabled:opacity-30"><ChevronDown size={13} /></button>
                      <button onClick={() => setDelTarget(p)} data-testid={`path-del-${p.id}`} className="v2-btn-ghost v2-btn-sm text-[#F7A08A]"><Trash2 size={13} /></button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {(p.challenges || []).map((ch, i) => (
                      <div key={ch.id} className="flex items-center gap-2 v2-inset px-3 py-2" data-testid={`path-ch-${ch.id}`}>
                        <span className="w-6 h-6 rounded-full bg-[#bb9661]/15 text-[#e3c799] text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        <span className="flex-1 min-w-0 text-sm truncate">{ch.title}</span>
                        <AudienceChips value={ch.audience || []} />
                        <Chip tone="muted">{ch.points} نقطة</Chip>
                        <button onClick={() => moveChallenge(p.id, p.challenges, i, -1)} disabled={i === 0}
                          className="text-[#bcc8cf] hover:text-white disabled:opacity-30"><ChevronUp size={14} /></button>
                        <button onClick={() => moveChallenge(p.id, p.challenges, i, 1)} disabled={i === p.challenges.length - 1}
                          className="text-[#bcc8cf] hover:text-white disabled:opacity-30"><ChevronDown size={14} /></button>
                        <button onClick={() => setAssign({ ...ch, path_id: p.id })} data-testid={`path-ch-assign-${ch.id}`}
                          className="text-[#bcc8cf] hover:text-[#e3c799]"><Link2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              ))}
            </div>
          ))}

          {(data?.unassigned_challenges || []).length > 0 && (
            <GlassCard className="p-5" data-testid="unassigned-block">
              <h3 className="font-extrabold mb-3">تحديات غير مرتبطة بمسار</h3>
              <div className="space-y-2">
                {data.unassigned_challenges.map((ch) => (
                  <div key={ch.id} className="flex items-center gap-3 v2-inset px-3 py-2">
                    <span className="flex-1 text-sm truncate">{ch.title}</span>
                    <AudienceChips value={ch.audience || []} />
                    <button onClick={() => setAssign(ch)} data-testid={`assign-${ch.id}`} className="v2-btn-ghost v2-btn-sm">
                      <Link2 size={13} /> ربط بمسار
                    </button>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </div>
      )}

      {modal && <PathModal item={modal.id ? modal : null} comps={comps} onClose={() => setModal(null)} onSaved={refresh} />}
      {assign && <AssignModal challenge={assign} paths={allPaths} onClose={() => setAssign(null)} onSaved={refresh} />}
      {preview && <PreviewModal type={preview} onClose={() => setPreview("")} />}
      {delTarget && (
        <Confirm title="حذف المسار" body={`سيتم حذف «${delTarget.name}». لا يمكن حذف مسار مرتبط بتحديات.`}
          confirmLabel="حذف" danger onConfirm={removePath} onClose={() => setDelTarget(null)} testId="path-del-confirm" />
      )}
    </div>
  );
}
